import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList, 
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Card } from "../ui/card";
import { ChevronDown, Filter, MapPin, Star, Sparkles } from "lucide-react";
import { Separator } from "../ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible"; 
import { StarFilledIcon } from "@radix-ui/react-icons"; 
import { useLocation, useNavigate } from "react-router-dom";
import { ApiContext } from "src/context/ApiContext.jsx"; 
import Fuse from "fuse.js";
import { toast, Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const Search = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedCategoryId = queryParams.get("category");
  const selectedServiceId = queryParams.get("service");
  const query = queryParams.get("query");
  
  const { subservice, city } = useContext(ApiContext);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // State
  const [filteredSubService, setFilteredSubService] = useState([]); 
  const [selectedPriceRange, setSelectedPriceRange] = useState(null); 
  const [selectedRating, setSelectedRating] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Price Ranges
  const priceRanges = useMemo(() => [
    [0, 1000], [1000, 2000], [2000, 3000], [3000, 4000], [4000, 5000]
  ], []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Filter and sort logic
  useEffect(() => {
    let filtered = [...subservice];
    
    if (selectedServiceId) {
      filtered = filtered.filter(s => s?.service?._id === selectedServiceId);
    } else if (selectedCategoryId) {
      filtered = filtered.filter(
        s => s?.service?.subCategory?.category?._id === selectedCategoryId
      );
    } else if (query) {
      const fuse = new Fuse(filtered, { keys: ["name"], threshold: 0.3 });
      filtered = fuse.search(query).map(res => res.item);
    }

    // Apply price filter
    if (selectedPriceRange?.length === 2) {
      filtered = filtered.filter(
        p => p.price >= selectedPriceRange[0] && p.price < selectedPriceRange[1]
      );
    }

    // Apply rating filter
    if (selectedRating) {
      filtered = filtered.filter(
        p => Math.round(parseFloat(p.averageRating)) === selectedRating
      );
    }

    // Apply sorting
    if (sortOrder === "low-to-high") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOrder === "high-to-low") {
      filtered.sort((a, b) => b.price - a.price);
    }

    setFilteredSubService(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedServiceId, selectedCategoryId, query, selectedPriceRange, selectedRating, sortOrder, subservice]);

  // Pagination
  const paginatedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSubService.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSubService, currentPage]);

  const totalPages = Math.ceil(filteredSubService.length / itemsPerPage);

  // Handlers
  const handleRatingSelect = (rating) => { 
    setSelectedRating(selectedRating === rating ? null : rating);
  };

  const handleSubServices = (subservice) => {
    navigate(`/service_details?subservice=${subservice?._id}`);
  };

  const handleServiceDetail = (subserviceId) => {
    if (!token) {
      toast.error("Please Login to Book the service");
      return;
    }
    sessionStorage.setItem("subserviceId", subserviceId);
    navigate(`/checkout?subservice=${subserviceId}`);
  };

  const resetFilter = () => {
    setSelectedPriceRange(null);
    setSelectedRating(null);
  };

  return (
    <div className="px-[3vw] min-h-screen bg-gradient-to-b from-gray-100 to-white">
      <Toaster richColors position="top-center" />
      
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              x: [0, 100 * (i % 2 === 0 ? 1 : -1)],
              y: [0, 50 * (i % 3 === 0 ? 1 : -1)],
              rotate: [0, 360],
              opacity: [0.05, 0.15, 0.05]
            }}
            transition={{
              duration: 30 + i * 5,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute rounded-full bg-gradient-to-r from-blue-100/50 to-purple-100/50"
            style={{
              width: `${150 + i * 30}px`,
              height: `${150 + i * 30}px`,
              top: `${10 + i * 10}%`,
              left: `${10 + (i * 12) % 80}%`,
              filter: "blur(60px)"
            }}
          />
        ))}
      </div>

      <div className="relative z-10">
        {/* Breadcrumb */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="pt-24"
        >
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="hover:text-primary transition-colors">
                  Home
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-400" />
              <BreadcrumbItem>
                <BreadcrumbLink className="font-medium text-primary">
                  Services
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </motion.div>

        {/* Main Content */}
        <div className="my-7">
          <div className="flex gap-10 flex-col lg:flex-row">
            {/* Filters Sidebar */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:w-[25%] w-full"
            >
              <Card className="p-5 sticky top-5 border border-gray-200/50 bg-white/80 backdrop-blur-sm shadow-xl rounded-xl">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-2 items-center">
                    <Filter className="text-primary" />
                    <span className="text-xl font-bold text-gradient from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Filters
                    </span>
                  </div>
                  <button
                    onClick={resetFilter}
                    className="text-sm hover:underline cursor-pointer text-gray-500 hover:text-primary transition-colors"
                  >
                    Reset All
                  </button>
                </div>
                    
                <Separator className="my-4 bg-gray-200" />

                {/* Price Filter */}
                <div className="mb-6">
                  <Collapsible>
                    <CollapsibleTrigger className="text-lg font-semibold flex justify-between w-full items-center group">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                        <span className="group-hover:text-primary transition-colors">Price Range</span>
                      </div>
                      <ChevronDown className="text-gray-500 group-hover:text-primary transition-all" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2 pl-6">
                      {priceRanges.map((range, index) => (
                        <motion.div 
                          key={index}
                          whileHover={{ scale: 1.02 }}
                          className="flex gap-2 items-center mt-2"
                        >
                          <input
                            type="radio"
                            name="price"
                            className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                            checked={selectedPriceRange?.[0] === range[0]}
                            onChange={() => setSelectedPriceRange(range)}
                          />
                          <span className="text-gray-600">
                            ₹{range[0]} - ₹{range[1]}
                          </span>
                        </motion.div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                <Separator className="my-4 bg-gray-200" />

                {/* Rating Filter */}
                <div>
                  <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                    <CollapsibleTrigger className="text-lg font-semibold flex justify-between w-full items-center group">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="group-hover:text-primary transition-colors">Rating</span>
                      </div>
                      <ChevronDown
                        className={`text-gray-500 group-hover:text-primary transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2 pl-6">
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <motion.div
                          key={rating}
                          whileHover={{ scale: 1.02 }}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition ${
                            selectedRating === rating ? "bg-blue-50" : "hover:bg-gray-50"
                          }`}
                          onClick={() => handleRatingSelect(rating)}
                        >
                          <input
                            type="radio"
                            name="rating"
                            className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                            checked={selectedRating === rating}
                            onChange={() => handleRatingSelect(rating)}
                          />
                          <div className="flex items-center gap-1">
                            {[...Array(rating)].map((_, i) => (
                              <StarFilledIcon key={i} className="w-4 h-4 text-yellow-500" />
                            ))}
                          </div>
                          
                        </motion.div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </Card>
            </motion.div>

            {/* Results Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:w-[75%] w-full"
            >
              <Card className="p-6 border border-gray-200/50 bg-white/80 backdrop-blur-sm shadow-xl rounded-xl">
                {/* Results Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                  <motion.div whileHover={{ scale: 1.01 }}>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      Found <span className="text-transparent text-gradient">{filteredSubService.length} Sub-Services</span> 
                    </h2>
                    {city && (
                      <div className="flex items-center gap-1 mt-1 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>Near {city}</span>
                      </div>
                    )}
                  </motion.div>
                  
                  <div className="flex gap-3 items-center">
                    <span className="text-gray-600">Sort by:</span>
                    <select
                      className="border border-gray-300 py-2 px-4 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white"
                      onChange={(e) => setSortOrder(e.target.value)}
                      value={sortOrder}
                    >
                      <option value="">Recommended</option>
                      <option value="low-to-high">Price: Low to High</option>
                      <option value="high-to-low">Price: High to Low</option>
                    </select>
                  </div>
                </div>

                {/* Services Grid */}
                {paginatedServices.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                      <AnimatePresence>
                        {paginatedServices.map((subservice) => (
                          <motion.div
                            key={subservice._id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300 }}
                            whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                            className="relative"
                          >
                            <Card className="h-full overflow-hidden border border-gray-200/50 hover:border-primary/30 transition-all group cursor-pointer"
                            onClick={() => handleSubServices(subservice)}
                            >
                              {/* Image with hover effect */}
                              <div className="relative aspect-video overflow-hidden">
                                <img
                                  src={subservice.icon?.[0] || "https://via.placeholder.com/300"}
                                  alt={subservice.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  onClick={() => handleSubServices(subservice)}
                                  onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/300";
                                  }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                                {/* Rating badge */}
                                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                  <StarFilledIcon className="text-yellow-500 w-4 h-4" />
                                  <span className="text-sm font-medium">{subservice.averageRating || "4.5"}</span>
                                  <span className="text-xs text-gray-500">({subservice.reviews?.length || 0})</span>
                                </div>
                              </div>

                              {/* Content */}
                              <div className="p-4">
                                <h3 
                                  className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 hover:text-primary transition-colors cursor-pointer"
                                  
                                >
                                  {subservice.name}
                                </h3>
                                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                  {subservice.description}
                                </p>

                                <div className="flex items-center justify-between mt-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-2xl font-bold text-gradient bg-clip-text text-transparent">
                                      ₹{subservice.price}
                                    </span> 
                                  </div>
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-4 py-2 bg-black text-white rounded-lg shadow-md hover:shadow-lg transition-all"
                                    onClick={() => handleServiceDetail(subservice._id)}
                                  >
                                    Book Now
                                  </motion.button>
                                </div>
                              </div>

                              {/* Floating particles on hover */}
                              {/* <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                {[...Array(6)].map((_, i) => (
                                  <motion.div
                                    key={i}
                                    initial={{ opacity: 0 }}
                                    animate={{
                                      opacity: [0, 0.8, 0],
                                      y: [0, -20, 0],
                                      x: [0, Math.random() * 40 - 20, 0]
                                    }}
                                    transition={{
                                      duration: 2 + Math.random() * 3,
                                      repeat: Infinity,
                                      delay: i * 0.3
                                    }}
                                    className="absolute text-yellow-400 text-xl"
                                    style={{
                                      left: `${Math.random() * 100}%`,
                                      top: `${Math.random() * 100}%`
                                    }}
                                  >
                                    {["✧", "✦", "❂", "✺", "✶"][i % 5]}
                                  </motion.div>
                                ))}
                              </div> */}
                            </Card>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center mt-10">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            Previous
                          </button>

                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-4 py-2 rounded-lg transition-all ${
                                  currentPage === pageNum
                                    ? "gradient text-white shadow-md"
                                    : "border border-gray-300 hover:bg-gray-100"
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          {totalPages > 5 && currentPage < totalPages - 2 && (
                            <span className="px-4 py-2">...</span>
                          )}

                          <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-5xl mb-4">🔍</div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">No services found</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your filters or search term</p>
                    <button
                      onClick={resetFilter}
                      className="px-6 py-3 gradient text-white rounded-lg shadow-lg hover:shadow-xl transition-all"
                    >
                      Reset Filters
                    </button>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;